import {
  ActionList,
  ActionListItem,
  Alert,
  Box,
  Button,
  Divider,
  Dropdown,
  DropdownOverlay,
  Link,
  Radio,
  RadioGroup,
  SelectInput,
  Text,
  useToast,
} from '@razorpay/blade/components';
import api from 'api';
import { routePaths } from 'components/Routes/data';
import { salaryComponentParams } from 'components/SalaryComponents/constants';
import { useDetailsView } from 'components/SalaryComponents/hooks';
import {
  FieldGroup,
  FormWrapper,
  StepActionsWrapper,
  StepContentWrapper,
  StepHeader,
  SwitchDescription,
  SwitchField,
} from 'components/WizardViews/components';
import {
  earningsTaxSections,
  knowMoreItems,
  overtimeValidationSchema,
  taxDeductionBehavior,
} from 'components/WizardViews/SalaryComponents/constants';
import { isTaxExempt } from 'components/WizardViews/SalaryComponents/utils';
import { useFormWithProvider } from 'forms';
import React, { FormEvent, useMemo } from 'react';
import { useMutation } from 'react-query';
import { useHistory } from 'react-router-dom';
import { AppError } from 'utils/AppError';
import { OvertimeFormData } from './types';
import { convertFormDataToOTUpdatePayload } from './utils';

const GeneralDetailsStep: React.FC = () => {
  const { formData, validate, setIsSubmitting, register } = useFormWithProvider<OvertimeFormData>();
  const history = useHistory();
  const toast = useToast();
  const { openDetailsView } = useDetailsView(salaryComponentParams.knowMore);

  const updateComponent = useMutation({
    mutationFn: api.overtime.mutations.updateComponent,
    onError: (e: AppError) => {
      toast.show({
        content: 'There was an error in updating the overtime component.',
        color: 'negative',
        duration: 3000,
      });
    },
    onMutate() {
      setIsSubmitting(true);
    },
    onSettled() {
      setIsSubmitting(false);
    },
    onSuccess() {
      toast.show({
        content: 'Overtime component updated successfully!',
        color: 'positive',
      });
      history.push(routePaths.salaryComponents.root);
    },
  });

  const isFormValid = useMemo(
    () => validate(overtimeValidationSchema, { sideEffects: false }),
    [validate],
  );

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (validate(overtimeValidationSchema)) {
      updateComponent.mutate(convertFormDataToOTUpdatePayload(formData));
    }
  };

  const isExemptionVisible = isTaxExempt(
    formData.taxExemptInNewRegime,
    formData.taxExemptInOldRegime,
  );

  return (
    <FormWrapper onSubmit={handleSubmit}>
      <StepContentWrapper>
        <StepHeader title="General & pay" subtitle="Configuration" marginBottom={'spacing.9'} />

        <Box marginBottom="spacing.9">
          <Box
            display="grid"
            gridTemplateColumns="1fr 1fr 1fr"
            rowGap="spacing.7"
            columnGap="spacing.8">
            <Box>
              <Text color="surface.text.gray.muted" size="small">
                Component Name
              </Text>
              <Text color="surface.text.gray.subtle" weight="medium">
                {formData.name}
              </Text>
            </Box>
            <Box>
              <Text color="surface.text.gray.muted" size="small">
                Display Name
              </Text>
              <Text color="surface.text.gray.subtle" weight="medium">
                {formData.displayName}
              </Text>
            </Box>
            <Box>
              <Text color="surface.text.gray.muted" size="small">
                Description
              </Text>
              <Text color="surface.text.gray.subtle" weight="medium">
                {formData.description || '-'}
              </Text>
            </Box>
          </Box>
        </Box>

        <Divider marginY="spacing.8" />

        <FieldGroup title="Proration & Arrears">
          <SwitchField
            label="Prorate the component"
            accessibilityLabel="Proration"
            size="small"
            tooltip={'Ad hoc components cannot be prorated'}
            {...register('supportProration', 'switch')}
            isDisabled={true}>
            <SwitchDescription content="The pay will be adjusted based on the number of working days, such as for loss of pay, joining date." />
            <Link
              variant="button"
              marginLeft="spacing.2"
              size="small"
              onClick={() => openDetailsView(knowMoreItems.proration)}>
              Know more✨
            </Link>
          </SwitchField>
          <SwitchField
            label="Calculate arrears"
            accessibilityLabel="Calculate Arrears"
            size="small"
            {...register('considerInArrear', 'switch')}>
            <SwitchDescription content="Choose whether to calculate arrears for this component in case of missed payments or salary revisions." />
            <Link
              variant="button"
              marginLeft="spacing.2"
              size="small"
              onClick={() => openDetailsView(knowMoreItems.arrearCalculation)}>
              Know more✨
            </Link>
          </SwitchField>
        </FieldGroup>

        <Divider marginY="spacing.8" />

        <FieldGroup
          title="Wage calculation"
          subtitle="Define if the component is included in the calculation of statutory wages."
          infoLabel="Know more✨"
          onInfoClick={() => openDetailsView(knowMoreItems.wageCalculation)}>
          <SwitchField
            label="Part of PF wage"
            accessibilityLabel="Part of PF wage"
            size="small"
            {...register('includeInPfWages', 'switch')}
          />
          <SwitchField
            label="Part of ESI wage"
            accessibilityLabel="Part of ESI wage"
            size="small"
            {...register('includeInEsiWages', 'switch')}
          />
          <SwitchField
            label="Part of PT wage"
            accessibilityLabel="Part of PT wage"
            size="small"
            {...register('includeInPtWages', 'switch')}
          />
          <SwitchField
            label="Part of LWF wage"
            accessibilityLabel="Part of LWF wage"
            size="small"
            {...register('includeInLwfWages', 'switch')}
          />
          <SwitchField
            label="Part of Gratuity wages"
            accessibilityLabel="Part of Gratuity wages"
            size="small"
            {...register('includeInGratuityWages', 'switch')}
          />
          <SwitchField
            label="Part of Leave Encashment wages"
            accessibilityLabel="Part of Leave Encashment wages"
            size="small"
            {...register('includeInLeaveEncashmentWages', 'switch')}
          />
        </FieldGroup>

        <Divider marginY="spacing.8" />

        <Alert
          marginBottom="spacing.7"
          isFullWidth
          title="Modify details"
          isDismissible={false}
          description="If taxability is updated, the change will apply retrospectively to all previous payrolls within the same financial year for the component."
        />

        <FieldGroup
          title="Taxability"
          subtitle="Choose if the component is taxable in the respective income tax regimes."
          infoLabel="Know more✨"
          onInfoClick={() => openDetailsView(knowMoreItems.taxability)}>
          <SwitchField
            label="Exempt in the old tax regime"
            accessibilityLabel="Exempt in the old tax regime"
            size="small"
            {...register('taxExemptInOldRegime', 'switch')}
          />
          <SwitchField
            label="Exempt in the new tax regime"
            accessibilityLabel="Exempt in the new tax regime"
            size="small"
            {...register('taxExemptInNewRegime', 'switch')}
          />
          {isExemptionVisible && (
            <Dropdown>
              <SelectInput
                label="Exempt under which section"
                placeholder="Exempt under which section"
                {...register('taxExemptionSection', 'select')}
              />
              <DropdownOverlay>
                <ActionList>
                  {earningsTaxSections.map((section) => (
                    <ActionListItem
                      key={section.key}
                      title={section.label}
                      value={section.key}
                      description={section.description}
                    />
                  ))}
                </ActionList>
              </DropdownOverlay>
            </Dropdown>
          )}
        </FieldGroup>

        <Divider marginY="spacing.8" />

        <FieldGroup
          title="Tax deduction"
          subtitle="Define when to deduct the tax."
          infoLabel="Know more✨"
          onInfoClick={() => openDetailsView(knowMoreItems.taxDeductions)}>
          <RadioGroup {...register('taxDeductionBehaviour', 'radiogroup')}>
            <Radio value="pro_rated" helpText={taxDeductionBehavior.pro_rated.helpText}>
              {taxDeductionBehavior.pro_rated.label}
            </Radio>
            <Radio value="instant" helpText={taxDeductionBehavior.instant.helpText}>
              {taxDeductionBehavior.instant.label}
            </Radio>
          </RadioGroup>
        </FieldGroup>
      </StepContentWrapper>
      <StepActionsWrapper>
        <span />
        <Button type="submit" isDisabled={!isFormValid} isLoading={updateComponent.isLoading}>
          Save component
        </Button>
      </StepActionsWrapper>
    </FormWrapper>
  );
};

export default GeneralDetailsStep;
